import React from "react";
import usePlanningWriteActivity from "../hooks/usePlanningWriteActivity.js";
import PlanningWriteActivityNotice from "./PlanningWriteActivityNotice.jsx";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react-dom";
import { ChevronDown } from "lucide-react";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import ConfirmationModal from "./ConfirmationModal.jsx";
import { HoverCard } from "./resultsPrimitives/hoverCardPrimitives.jsx";
import AssignmentOptionDetails from "./AssignmentOptionDetails.jsx";
import { driverOptionDetails } from "../utils/assignmentOptionDetails.js";
import {
  formatDriverSelectOptionLabel,
  resolveDriverSelectOptionParts,
  loadDriverCatalog,
  saveDefaultDriver,
  driverTransferConfirmation,
  confirmedDriverTransfer,
} from "../lib/driverAssignmentsApi.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";

function DriverOptionColumns({ name, truckLabel = "", muted = false, selected = false }) {
  return <>
    <span className={`min-w-0 flex-1 truncate ${muted ? "text-content-muted" : ""}`}>{name}</span>
    {truckLabel ? <span className={`shrink-0 text-right tabular-nums ${selected ? "" : "text-content-muted"}`}>{truckLabel}</span> : null}
  </>;
}

function DispatchDriverEmployeeSelect({
  ariaLabel,
  catalog,
  currentName = "",
  disabled = false,
  error = "",
  dispatchPeriod = "day",
  onChange,
  truckId = "",
  value = "",
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerRef = React.useRef(null);
  const listId = React.useId();
  const unresolved = !value && currentName;
  const employees = catalog?.employees || [];
  const options = React.useMemo(() => {
    const rows = [];
    if (unresolved) rows.push({ id: "__unresolved__", name: currentName, truckLabel: "", disabled: true });
    rows.push({ id: "", name: "Aucun chauffeur", truckLabel: "" });
    employees.forEach((person) => {
      const parts = resolveDriverSelectOptionParts(person);
      rows.push({ id: person.employee_id, name: parts.name, truckLabel: parts.assignedTruckLabel, person });
    });
    return rows;
  }, [currentName, employees, unresolved]);
  const selected = options.find((option) => option.id === (unresolved ? "__unresolved__" : value || "")) || options[0];
  const selectedIndex = Math.max(0, options.findIndex((option) => option.id === selected?.id));
  const [highlightedIndex, setHighlightedIndex] = React.useState(selectedIndex);
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isOpen,
    strategy: "fixed",
    placement: "bottom-start",
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, { width: `${Math.round(rects.reference.width)}px` });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const close = React.useCallback(() => setIsOpen(false), []);
  usePopoverDismiss({
    anchorElement: triggerRef.current,
    floatingRef: refs.floating,
    isOpen,
    onClose: close,
  });
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      close();
    };
    document.addEventListener("keydown", onEscape, true);
    return () => document.removeEventListener("keydown", onEscape, true);
  }, [close, isOpen]);
  React.useEffect(() => {
    if (isOpen) setHighlightedIndex(selectedIndex);
  }, [isOpen, selectedIndex]);
  const choose = (option) => {
    if (!option || option.disabled) return;
    onChange?.(option.id || null, option.person, catalog?.assignments?.[truckId]);
    close();
  };
  const moveHighlight = (delta) => {
    const enabled = options.map((option, index) => (option.disabled ? -1 : index)).filter((index) => index >= 0);
    if (!enabled.length) return;
    const current = enabled.includes(highlightedIndex) ? highlightedIndex : (enabled[0] ?? 0);
    const position = enabled.indexOf(current);
    setHighlightedIndex(enabled[(position + delta + enabled.length) % enabled.length]);
  };
  const triggerLabel = !catalog
    ? (error ? "État indisponible" : "Chargement des chauffeurs…")
    : (selected?.name || "Aucun chauffeur");
  const portalRoot = getReactPortalRoot();
  return <div className="grid min-w-0 gap-1">
    <button
      ref={(element) => { triggerRef.current = element; refs.setReference(element); }}
      type="button"
      aria-controls={listId}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-label={ariaLabel}
      data-driver-employee-select
      className="flex h-8 w-full min-w-0 items-center gap-2 rounded-[4px] border border-line-soft bg-white px-2 text-left text-xs text-content-ink focus:border-accent focus:outline-none disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
      disabled={disabled || !catalog}
      onClick={() => setIsOpen((current) => !current)}
      onKeyDown={(event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (!isOpen) setIsOpen(true);
          else moveHighlight(event.key === "ArrowDown" ? 1 : -1);
        } else if (event.key === "Enter" && isOpen) {
          event.preventDefault();
          choose(options[highlightedIndex]);
        }
      }}
    >
      <DriverOptionColumns name={triggerLabel} truckLabel={catalog ? selected?.truckLabel : ""} muted={!catalog} />
      <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-content-muted" />
    </button>
    {isOpen && portalRoot ? createPortal(
      <ul
        ref={refs.setFloating}
        id={listId}
        role="listbox"
        aria-label={ariaLabel}
        data-driver-employee-select-list="true"
        className="z-popover max-h-64 overflow-y-auto rounded-[4px] border border-line-soft bg-white py-1 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
        style={{ ...floatingStyles, visibility: isPositioned ? "visible" : "hidden", zIndex: 2200 }}
      >
        {options.map((option, index) => {
          const isSelected = option.id === selected?.id;
          const isHighlighted = index === highlightedIndex;
          const optionButton = <button
              type="button"
              role="option"
              aria-selected={isSelected}
              disabled={option.disabled}
              className={[
                "flex w-full items-center gap-3 px-2 py-1 text-left text-xs",
                option.disabled ? "cursor-not-allowed text-content-muted" : "hover:bg-slate-50",
                isSelected ? "bg-accent text-white hover:bg-accent-strong" : "",
                isHighlighted && !isSelected ? "bg-slate-50" : "",
              ].filter(Boolean).join(" ")}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => choose(option)}
            >
              <DriverOptionColumns
                name={option.name}
                truckLabel={option.truckLabel}
                muted={option.disabled}
                selected={isSelected}
              />
            </button>;
          return <li key={`${option.id || "none"}-${index}`} role="presentation">
            {option.person ? <HoverCard referenceAsChild closeOnReferenceClick closeOnScroll placement="right"
              title={option.name} content={<AssignmentOptionDetails rows={driverOptionDetails(option.person, dispatchPeriod)} />}>
              {optionButton}
            </HoverCard> : optionButton}
          </li>;
        })}
      </ul>,
      portalRoot,
    ) : null}
  </div>;
}

export function DriverEmployeeSelect({ value = "", currentName = "", truckId = "", dispatchDate = "", dispatchPeriod = "day", scope = "dispatch", disabled = false, onChange, onCatalog, refreshKey = 0 }) {
  const [catalog, setCatalog] = React.useState(null);
  const [error, setError] = React.useState("");
  const onCatalogRef = React.useRef(onCatalog);
  const catalogContextRef = React.useRef("");
  onCatalogRef.current = onCatalog;
  React.useEffect(() => {
    const controller = new AbortController();
    const contextKey = `${dispatchDate}:${dispatchPeriod}:${scope}`;
    if (catalogContextRef.current !== contextKey) setCatalog(null);
    catalogContextRef.current = contextKey;
    setError("");
    loadDriverCatalog({ dispatchDate, dispatchPeriod, scope, signal: controller.signal }).then((data) => {
      setCatalog(data); onCatalogRef.current?.(data);
    }).catch((failure) => { if (failure.name !== "AbortError") setError(failure.message); });
    return () => controller.abort();
  }, [dispatchDate, dispatchPeriod, scope, refreshKey]);
  const ariaLabel = `Chauffeur ${dispatchPeriod === "evening" ? "Soir" : "Jour"}`;
  if (scope === "dispatch") {
    return <>
      <DispatchDriverEmployeeSelect
        ariaLabel={ariaLabel}
        catalog={catalog}
        currentName={currentName}
        dispatchPeriod={dispatchPeriod}
        disabled={disabled}
        error={error}
        onChange={onChange}
        truckId={truckId}
        value={value}
      />
      {error ? <p role="alert" className="text-xs text-red-700">{error}</p> : null}
    </>;
  }
  const unresolved = !value && currentName;
  return <div className="grid min-w-0 gap-1">
    <select aria-label={ariaLabel} data-driver-employee-select
      data-grid-focus-key={scope === "defaults" ? (dispatchPeriod === "evening" ? "evening_driver_employee_id" : "driver_employee_id") : undefined}
      className={`w-full min-w-0 rounded-[4px] border border-line-soft bg-white px-2 text-xs text-content-ink focus:border-accent focus:outline-none ${scope === "defaults" ? "h-6 py-0" : "h-8"}`}
      disabled={disabled || !catalog} value={unresolved ? "__unresolved__" : value || ""}
      onChange={(event) => onChange?.(event.target.value || null, catalog?.employees?.find((person) => person.employee_id === event.target.value), catalog?.assignments?.[truckId])}>
      {!catalog ? <option value="">{error ? "État indisponible" : "Chargement des chauffeurs…"}</option> : null}
      {unresolved ? <option value="__unresolved__" disabled>{currentName} — À confirmer</option> : null}
      <option value="">Aucun chauffeur</option>
      {(catalog?.employees || []).map((person) => <option key={person.employee_id} value={person.employee_id}>
        {formatDriverSelectOptionLabel(person)}
      </option>)}
    </select>
    {error ? <p role="alert" className="text-xs text-red-700">{error}</p> : null}
  </div>;
}

export default function DriverAssignmentEditor({ assignment, truckId, currentName = "", dispatchDate = "", dispatchPeriod = "day", scope = "dispatch", onCommit, onSaved, onDraftStateChange, saveLabel = "Enregistrer" }) {
  const planningWriteMessage = usePlanningWriteActivity();
  const planningWriteBlocked = scope === "dispatch" && Boolean(planningWriteMessage);
  const [selection, setSelection] = React.useState(undefined);
  const [version, setVersion] = React.useState(assignment?.driver_assignment_version);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [confirmation, setConfirmation] = React.useState(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const currentId = assignment?.driver_employee_id || "";
  const selectedId = selection === undefined ? currentId : selection || "";
  const unresolvedName = selection === undefined && !currentId ? currentName : "";
  const draftCallbackRef = React.useRef(onDraftStateChange);
  draftCallbackRef.current = onDraftStateChange;
  const dirty = selection !== undefined && selectedId !== currentId;
  React.useEffect(() => {
    if (scope === "defaults" && !busy && selection !== undefined && selectedId === currentId) {
      setSelection(undefined);
    }
  }, [busy, currentId, scope, selectedId, selection]);
  React.useEffect(() => { draftCallbackRef.current?.({ dirty, busy }); }, [dirty, busy]);
  React.useEffect(() => () => { draftCallbackRef.current?.({ dirty: false, busy: false }); }, []);
  const handleSelectionChange = (employeeId, _person, current) => {
    const nextVersion = current?.driver_assignment_version;
    setSelection(employeeId);
    setVersion(nextVersion);
    if (scope !== "defaults" || (employeeId || "") === currentId) {
      return;
    }
    void submit({
      dispatch_period: dispatchPeriod,
      employee_id: employeeId || null,
      expected_assignment_version: nextVersion,
    });
  };
  const submit = async (confirmedBody = null) => {
    if (planningWriteBlocked) return;
    const body = confirmedBody || { dispatch_period: dispatchPeriod, employee_id: selectedId || null, expected_assignment_version: version };
    setBusy(true); setError("");
    try {
      const result = onCommit ? await onCommit({ employeeId: body.employee_id, expectedAssignmentVersion: body.expected_assignment_version, dispatchPeriod, truckId }) : await saveDefaultDriver(truckId, body);
      if (result) {
        onSaved?.(result, body.employee_id);
        window.dispatchEvent(new CustomEvent("grain-driver-assignments-changed", { detail: { scope, truckIds: result.modified_truck_ids || [] } }));
        if (scope !== "defaults") setSelection(undefined);
        setRefreshKey((value) => value + 1);
      }
    } catch (failure) {
      if (failure.challenge) {
        setConfirmation({ ...driverTransferConfirmation(failure.challenge), isOpen: true,
          onConfirm: () => { setConfirmation(null); void submit(confirmedDriverTransfer(body, failure.challenge)); },
          onCancel: () => setConfirmation(null), onClose: () => setConfirmation(null) });
      } else { setError(failure.message); setRefreshKey((value) => value + 1); }
    } finally { setBusy(false); }
  };
  return <div className={scope === "defaults" ? "min-w-0" : "grid gap-2 rounded-[4px] border border-line-soft bg-white p-2"} data-driver-assignment-editor>
    <DriverEmployeeSelect value={selectedId} currentName={unresolvedName} truckId={truckId} dispatchDate={dispatchDate} dispatchPeriod={dispatchPeriod} scope={scope} disabled={busy} refreshKey={refreshKey}
      onCatalog={(data) => setVersion(data.assignments?.[truckId]?.driver_assignment_version)}
      onChange={handleSelectionChange} />
    {scope === "dispatch" && !busy ? <PlanningWriteActivityNotice /> : null}
    {scope === "dispatch" ? <div className="flex items-center gap-2">
      <button type="button" className="h-7 rounded-[4px] border border-accent bg-accent px-2 text-xs font-semibold text-white hover:border-accent-strong hover:bg-accent-strong disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none" disabled={busy || planningWriteBlocked || selection === undefined || !version} onClick={() => void submit()}>{busy ? "Enregistrement…" : saveLabel}</button>
    </div> : null}
    {error ? <p role="alert" className="text-xs text-red-700">{error}</p> : null}
    {confirmation ? <ConfirmationModal {...confirmation} /> : null}
  </div>;
}
